import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ContentListItem from "@/components/feature/ContentListItem";
import EmptyState from "@/components/feature/EmptyState";
import Toast from "@/components/feature/Toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getItemById, getRelatedItems } from "@/utils/search";
import { formatDate, typeLabel, typePath } from "@/utils/format";
import VideoPlayerDemo from "./components/VideoPlayerDemo";

export default function Detail() {
  const { type = "", id = "" } = useParams();
  const navigate = useNavigate();
  const item = getItemById(type, id);

  const [toast, setToast] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const timer = useRef<number | null>(null);

  useDocumentMeta({
    title: item ? `${item.title} - ${typeLabel(item.type)}详情 - 百度一下` : "内容详情 - 百度一下",
    description: item?.summary,
    keywords: item ? [item.title, ...item.tags, "百度一下"].join(",") : "百度一下",
  });

  const showToast = useCallback((message: string) => {
    setToast(message);
    setToastVisible(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToastVisible(false), 2000);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  if (!item) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-16 md:px-6">
          <EmptyState
            icon="ri-error-warning-line"
            title="内容加载失败"
            description="没有找到对应的内容，可能是链接有误或内容已下线。你可以返回上级列表继续浏览。"
            actions={
              <>
                <Link
                  to={type ? typePath(type) : "/"}
                  className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
                >
                  返回上级列表
                </Link>
                <Link
                  to="/"
                  className="rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  返回首页
                </Link>
              </>
            }
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedItems(item, 4);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 md:px-6 md:py-10">
        {/* Breadcrumb + back */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={typePath(item.type)} className="hover:text-primary-600">
              {typeLabel(item.type)}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">详情</span>
          </nav>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回上一级
          </button>
        </div>

        <article>
          <h1 className="font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
            {item.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {item.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line" />
              {item.source}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {formatDate(item.date)}
            </span>
            {item.views && (<span>{item.views}</span>)}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
              {item.category}
            </span>
            {item.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full border border-background-300 px-2.5 py-0.5 text-xs text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                #{tag}
              </Link>
            ))}
          </div>

          {/* Cover / player */}
          <div className="mt-6">
            {item.type === "video" ? (
              <VideoPlayerDemo poster={item.image} title={item.title} duration={item.duration} />
            ) : (
              <div className="aspect-video w-full overflow-hidden rounded-lg bg-background-100">
                <img
                  src={item.image}
                  alt={item.title}
                  title={`${item.title} - 百度一下`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            )}
          </div>

          {/* Body */}
          <div className="mt-6 flex flex-col gap-4 text-base leading-relaxed text-foreground-800">
            {item.content.map((p, idx) => (
              <p key={`${item.id}-p-${idx}`}>{p}</p>
            ))}
          </div>

          {/* Share */}
          <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-background-200 py-5">
            <span className="text-sm font-medium text-foreground-700">分享这篇内容：</span>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
            >
              <i className="ri-links-line" />
              复制链接
            </button>
          </div>
        </article>

        {/* Related */}
        <section className="mt-10">
          <h2 className="font-heading text-lg font-bold text-foreground-950">相关推荐</h2>
          <div className="mt-4 flex flex-col gap-4">
            {related.map((r) => (
              <ContentListItem key={r.id} item={r} />
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
      <Toast message={toast} visible={toastVisible} />
    </div>
  );
}